import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Avatar from '@material-ui/core/Avatar';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import DoneIcon from '@material-ui/icons/Done';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import { getFullName, getInitials, toUserTimezone } from 'utils';
import stc from 'string-to-color';
import {bindMenu, bindTrigger, usePopupState} from "material-ui-popup-state/hooks";
import {useMutation} from "@apollo/react-hooks";
import {JOIN_CONVO} from "../../../../../graphql/conversations";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";
import MoreVertIcon from "@material-ui/icons/MoreHoriz";
import Menu from "@material-ui/core/Menu";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import NotifyModal from "./NotifyModal";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '10px',
    borderBottom: '1px solid #eee',
  },
  field: {
    'display': 'flex',
    'alignItems': 'center',
    'padding': 8,
    'borderBottom': '1px solid #eee',
    '& > span': {
      marginRight: 8,
    },
  },
  header: {
    'backgroundColor': (props) => (props.incoming ? '#3097F3' : '#d4d9dd'),
    'padding': '4px 8px',
    '& span': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      marginRight: 4,
      display: 'inline-block',
      backgroundColor: 'white',
    },
  },
  content: {
    padding: 8,
    paddingTop: 16,
    fontSize: 14,
  },
  footer: {
    marginTop: theme.spacing(2),
  },
  status: {
    marginRight: 4,
    height: 20,
    fontSize: 12,
  },
  chipIcon: {
    height: 16
  },
  preview: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
}));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    '&:focus': {
      'backgroundColor': theme.palette.primary.main,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const Email = ({ message, user, isFirst, isLast }) => {
  const [expanded, setExpanded] = useState(isLast);
  const [showHtml, setShowHtml] = useState(false);
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'messageMenu',
  });
  const [joinConvo] = useMutation(JOIN_CONVO);

  const {
    html,
    text,
    subject,
    convo_id,
    created_at,
    incoming,
    user: creator,
    receipts,
    attachments,
  } = message;
  const classes = useStyles({ incoming });
  const [showNotify, setShowNotify] = useState(false);

  const STATUS_ICON = {
    processed: <HourglassEmptyIcon color="action" />,
    delivered: <DoneIcon style={{ color: '#0088ff' }} />,
    open: <DoneAllIcon style={{ color: '#0088ff' }} />,
  };

  const toggleExpand = () => setExpanded(!expanded);

  const hideNotify = () => {
    setShowNotify(false);
    popupState.close();
  }

  const markUnread = () => {
    joinConvo({
      variables: {
        convoId: convo_id,
        userId: user.id,
        timestamp: moment(created_at)
          .subtract(1, 's')
          .toJSON(),
      },
    });
    popupState.close();
  };

  return (
    <Box className={classes.root}>
      <Box display="flex">
        <Box>
          <Avatar
            style={{
              marginRight: 8,
              alignSelf: 'center',
              backgroundColor: stc(creator.user_email),
            }}
            src={creator.avatar}
          >
            {getInitials(creator.user_first_name, creator.user_last_name)}
          </Avatar>
        </Box>
        <Box flex={1} overflow="hidden"  onClick={toggleExpand} style={{ cursor: 'pointer' }}>
          <Box display="flex">
            <Typography
              variant="subtitle2"
              style={{ marginRight: 'auto', color: '#333' }}
            >
              {getFullName(creator)}
            </Typography>
            <Typography color="textPrimary" variant="caption">
              {toUserTimezone(created_at, user.timezone).format('LLL')}
            </Typography>
          </Box>
          {expanded ? (
            <div>
              {receipts.length ? (
                <div>
                  {receipts.map(i =>
                    <Tooltip title={i.status} key={i.id}>
                      <Chip
                        classes={{
                          icon: classes.chipIcon
                        }}
                        className={classes.status}
                        clickable={false}
                        variant="outlined"
                        size="small"
                        icon={STATUS_ICON[i.status]}
                        label={i.user.user_first_name}
                      />
                    </Tooltip>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <Typography
              className={classes.preview}
              variant="body2"
              color="textPrimary"
            >
              {text}
            </Typography>
          )}
        </Box>
        <Box>
          {expanded && (
            <Box style={{ marginLeft: 8 }}>
              <IconButton size="small" {...bindTrigger(popupState)}>
                <MoreVertIcon />
              </IconButton>
              <Menu {...bindMenu(popupState)}>
                <StyledMenuItem onClick={markUnread}>
                  <ListItemText primary="Mark Unread" />
                </StyledMenuItem>
                {user.type === 'admin' && (
                  <StyledMenuItem onClick={() => setShowNotify(true)}>
                    <ListItemText primary="Notify Coaches" />
                  </StyledMenuItem>
                )}
              </Menu>
            </Box>
          )}
        </Box>
      </Box>
      {expanded && (
        <Box>
          {showHtml ? (
            <div
              className={classes.content}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <div className={classes.content} style={{ whiteSpace: 'pre' }}>
              {text}
            </div>
          )}
          <div className={classes.footer}>
            <Button
              fullWidth
              color="primary"
              variant="text"
              onClick={() => setShowHtml(!showHtml)}
            >
              {showHtml ? 'Show plain text' : 'Show original email'}
            </Button>
          </div>
        </Box>
      )}
      { attachments ? (
        <Box>
          <Typography variant="subtitle2">Attachments</Typography>
          {attachments.map(i => <p><a target="_blank" href={i.url}>{i.name}</a></p>)}
        </Box>
      ) : null}
      {showNotify && (
        <NotifyModal user={user} message={message} onClose={hideNotify} />
      )}
    </Box>
  );
};

export default Email;
